import { useState } from 'react';
import { Button, Modal, Typography, message, Tabs, Descriptions, Table } from 'antd';

import http from '../../http';
import { useTranslation } from 'react-i18next';
import { getReadableDateFromTimeStamp } from '../../utilities/common';

export const CalibrationCertificate = ({ record }) => {
    const [open, setIsOpen] = useState(false);
    const { t } = useTranslation();
    const handleDownload = async () => {
        const blob = await http
            .get(`/api/v1/machines/${record.id}/download_certificate/`, { responseType: 'blob' })
            .then((res) => res.data)
            .catch(() => {
                message.error(t('certificate_failed_to_download'));
            });
        if (!blob) return;
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `calibration-certificate-${record.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    let machine_data = {};

    try {
        machine_data = JSON.parse(record?.machine_data || '{}');
    } catch (e) {
        console.log(e);
    }

    const hasReport = Object.keys(machine_data?.calibration_report || {}).length > 0;

    console.log('machine_data', machine_data);

    return (
        <>
            <Button type='primary' onClick={() => setIsOpen(true)} style={{ marginTop: 8 }}>
                {t('certificate_view')}
            </Button>
            <Modal
                centered
                width={800}
                closable={true}
                open={open}
                okText={t('certificate_download')}
                cancelText='Close'
                cancelButtonProps={{ style: { display: 'none' } }}
                onOk={handleDownload}
                onCancel={() => setIsOpen(false)}
                okButtonProps={{ disabled: !hasReport }}
            >
                <Tabs
                    defaultActiveKey='1'
                    items={[
                        {
                            key: '1',
                            label: t('certificate_calibration'),
                            children: <CalibrationReport record={machine_data} />,
                        },
                        {
                            key: '2',
                            label: t('certificate_instrument'),
                            children: (
                                <InstrumentCertificate
                                    certificate_url={record?.instrument_certificate}
                                />
                            ),
                        },
                    ]}
                />
            </Modal>
        </>
    );
};

const { Title } = Typography;

function CalibrationReport({ record }) {
    const { t } = useTranslation();

    const measurementColumns = [
        {
            title: t('certificate_base_measurement_error'),
            children: [
                {
                    title: 'XY [m]',
                    dataIndex: 'base_error_xy',
                    key: 'base_error_xy',
                    render: (text) => (text !== undefined ? Number(text).toFixed(3) : '-'),
                },
                {
                    title: 'Z [m]',
                    dataIndex: 'base_error_z',
                    key: 'base_error_z',
                    render: (text) => (text !== undefined ? Number(text).toFixed(3) : '-'),
                },
            ],
        },
        {
            title: t('certificate_standard_deviation'),
            children: [
                {
                    title: 'XY [m]',
                    dataIndex: 'std_dev_xy',
                    key: 'std_dev_xy',
                    render: (text) => (text !== undefined ? Number(text).toFixed(3) : '-'),
                },
                {
                    title: 'Z [m]',
                    dataIndex: 'std_dev_z',
                    key: 'std_dev_z',
                    render: (text) => (text !== undefined ? Number(text).toFixed(3) : '-'),
                },
            ],
        },
    ];

    const columns = [
        {
            title: t('certificate_point_name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('certificate_mgs_measurement_error'),
            children: [
                {
                    title: 'XY [m]',
                    dataIndex: 'xy_error',
                    key: 'xy_error',
                    render: (text) => (text !== undefined ? Number(text).toFixed(3) : '-'),
                },
                {
                    title: 'Z [m]',
                    dataIndex: 'z_error',
                    key: 'z_error',
                    render: (text) => (text !== undefined ? Number(text).toFixed(3) : '-'),
                },
            ],
        },
        {
            title: t('certificate_residual_error_from_base'),
            children: [
                {
                    title: 'XY [m]',
                    dataIndex: 'xy_residual',
                    key: 'xy_residual',
                    render: (text) => (text !== undefined ? Number(text).toFixed(3) : '-'),
                },
                {
                    title: 'Z [m]',
                    dataIndex: 'z_residual',
                    key: 'z_residual',
                    render: (text) => (text !== undefined ? Number(text).toFixed(3) : '-'),
                },
            ],
        },
    ];

    const hasReport = Object.keys(record?.calibration_report || {}).length > 0;

    const pointData = record?.calibration_report?.check_points ?? [];
    const instrument_info = record?.calibration_report?.metadata?.measurement_instrument_info;
    const operator_info = record?.calibration_report?.metadata?.operator_info;
    const branch = operator_info?.branch_name;
    const pointMeasurementResults = [
        {
            key: '1',
            base_error_xy: record?.calibration_report?.base_error_xy,
            base_error_z: record?.calibration_report?.base_error_z,
            std_dev_xy: record?.calibration_report?.std_dev_xy,
            std_dev_z: record?.calibration_report?.std_dev_z,
        },
    ];

    return (
        <div>
            {hasReport ? (
                <>
                    <Descriptions
                        bordered
                        column={2}
                        size='small'
                        style={{ marginBottom: 24 }}
                        labelStyle={{ width: 200 }}
                    >
                        <Descriptions.Item label={t('certificate_issuer')} span={branch ? 1 : 2}>
                            {operator_info?.company_name}
                        </Descriptions.Item>
                        {branch && (
                            <Descriptions.Item label={t('certificate_branch')} span={1}>
                                {branch}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label={t('certificate_operator')} span={2}>
                            {operator_info?.name}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_date')}>
                            {getReadableDateFromTimeStamp(
                                record?.calibration_report?.metadata?.date
                            )}
                        </Descriptions.Item>
                    </Descriptions>

                    <Descriptions
                        bordered
                        column={1}
                        size='small'
                        style={{ marginBottom: 24 }}
                        labelStyle={{
                            width: 200,
                        }}
                    >
                        <Descriptions.Item label={t('certificate_model')}>
                            {/* {record?.machine_model} */}
                            {/* TODO: Now it's hardcoded later will be dynamic */}
                            MGS V3
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_manufacturer')}>
                            {/* {record?.machine_manufacture} */}
                            {/* TODO: Now it's hardcoded later will be dynamic */}
                            TOTALMASTERS Co. Ltd.
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_machine_id')}>
                            {record?.number}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_calibration_profile')}>
                            {record?.name?.replace('.json', '')}
                        </Descriptions.Item>
                    </Descriptions>
                    <Descriptions
                        bordered
                        column={2}
                        size='small'
                        style={{ marginBottom: 24 }}
                        labelStyle={{ width: 200 }}
                    >
                        <Descriptions.Item label={t('certificate_measurement_instrument')} span={1}>
                            {instrument_info?.model ?? '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_manufacturer')} span={1}>
                            {instrument_info?.name ?? '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('certificate_distance_accuracy')} span={2}>
                            {instrument_info?.accuracy}
                        </Descriptions.Item>
                    </Descriptions>

                    <Table
                        dataSource={pointMeasurementResults}
                        columns={measurementColumns}
                        pagination={false}
                        bordered
                        title={() => (
                            <Title level={5} style={{ margin: 0 }}>
                                {t('certificate_point_measurement_result')}
                            </Title>
                        )}
                    />

                    <Table
                        rowKey='name'
                        style={{ marginTop: 24 }}
                        dataSource={pointData}
                        columns={columns}
                        pagination={false}
                        bordered
                        title={() => (
                            <Title level={5} style={{ margin: 0 }}>
                                {t('certificate_point_measurements')}
                            </Title>
                        )}
                    />
                </>
            ) : (
                <Title
                    level={5}
                    style={{
                        textAlign: 'center',
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        padding: '24px 16px',
                        margin: 0,
                    }}
                >
                    {t('certificate_no_calibration_report')}
                </Title>
            )}
        </div>
    );
}

function InstrumentCertificate({ certificate_url }) {
    const { t } = useTranslation();

    return (
        <div
            style={
                !certificate_url
                    ? {
                          textAlign: 'center',
                          border: '1px solid #f0f0f0',
                          borderRadius: 8,
                          padding: '24px 16px',
                          margin: 0,
                      }
                    : { minHeight: 600 }
            }
        >
            {certificate_url ? (
                <iframe
                    src={certificate_url}
                    width='100%'
                    height='600px'
                    title='Instrument Certificate'
                    style={{ border: 'none' }}
                />
            ) : (
                <Title level={5} style={{ textAlign: 'center' }}>
                    {t('certificate_no_instrument_certificate')}
                </Title>
            )}
        </div>
    );
}
